import type { Command } from '../sim/core/commands';

/** What was under the tap, resolved by the scene from simulation state. */
export type TapTarget =
  | { readonly kind: 'ground' }
  | { readonly kind: 'unit'; readonly unitId: number; readonly own: boolean }
  | {
      readonly kind: 'building';
      readonly buildingId: number;
      readonly own: boolean;
      readonly mine: boolean;
      readonly underConstruction: boolean;
    }
  | { readonly kind: 'forest'; readonly x: number; readonly y: number };

/** A group of own units (1..MAX_SELECTION), one own building, or nothing. */
export type Selection =
  | { readonly kind: 'units'; readonly ids: readonly number[] }
  | { readonly kind: 'building'; readonly id: number }
  | null;

/** The guide's open question, answered: a phone-sized group. */
export const MAX_SELECTION = 12;

export interface TapResult {
  readonly selection: Selection;
  /** One command per selected unit; the simulation ignores ones a unit cannot follow. */
  readonly commands: readonly Command[];
}

/**
 * Selection and command rules for a single tap (CLAUDE.md §5 table). Pure: no Phaser
 * here, so it is unit-testable.
 *
 * - Tap an own unit → select just it (also narrows a group to it).
 * - Tap an own building → select it; except with units selected, tapping an own unfinished
 *   site sends them to build it.
 * - With units selected: ground → move; mine or forest → gather; enemy unit or building →
 *   attack. Every selected unit gets the command.
 * - With a building selected: ground → deselect.
 * - Anything else leaves the selection alone and issues nothing.
 */
export function resolveTap(
  selection: Selection,
  target: TapTarget,
  worldUnitsX: number,
  worldUnitsY: number,
  tick: number,
): TapResult {
  const ids = selection?.kind === 'units' ? selection.ids : [];
  const each = (make: (unitId: number) => Command): Command[] => ids.map(make);

  if (target.kind === 'unit') {
    if (target.own) return { selection: { kind: 'units', ids: [target.unitId] }, commands: [] };
    return {
      selection,
      commands: each((unitId) => ({ kind: 'attack', tick, unitId, targetId: target.unitId })),
    };
  }
  if (target.kind === 'building' && target.own && !target.mine) {
    if (target.underConstruction && ids.length > 0) {
      return {
        selection,
        commands: each((unitId) => ({
          kind: 'resumeBuild',
          tick,
          unitId,
          buildingId: target.buildingId,
        })),
      };
    }
    return { selection: { kind: 'building', id: target.buildingId }, commands: [] };
  }
  if (ids.length > 0) {
    if (target.kind === 'building' && !target.own && !target.mine) {
      return {
        selection,
        commands: each((unitId) => ({ kind: 'attack', tick, unitId, targetId: target.buildingId })),
      };
    }
    if (target.kind === 'building' && target.mine) {
      return {
        selection,
        commands: each((unitId) => ({
          kind: 'gather',
          tick,
          unitId,
          target: { kind: 'mine', buildingId: target.buildingId },
        })),
      };
    }
    if (target.kind === 'forest') {
      return {
        selection,
        commands: each((unitId) => ({
          kind: 'gather',
          tick,
          unitId,
          target: { kind: 'tree', x: target.x, y: target.y },
        })),
      };
    }
    if (target.kind === 'ground') {
      return {
        selection,
        commands: each((unitId) => ({
          kind: 'move',
          tick,
          unitId,
          x: worldUnitsX,
          y: worldUnitsY,
        })),
      };
    }
    return { selection, commands: [] };
  }
  if (selection?.kind === 'building' && target.kind === 'ground') {
    return { selection: null, commands: [] };
  }
  return { selection, commands: [] };
}
